import React, { useEffect, useState } from 'react'
import { Ticket, Plus, Printer, Trash2, RotateCcw, UserPlus, Shuffle } from 'lucide-react'
import api from '../api'
import { PageHeader, Badge, EmptyState, Table, td, Field, Button, Alert, Modal, ModalHeader, Input, Select, Pagination, Textarea } from '../components/ui'

const STATUS_TONE = { AKTIF: 'success', NONAKTIF: 'neutral' }

function rp(n) { return `Rp ${Number(n || 0).toLocaleString('id-ID')}` }

function defaultGenForm() {
  return { jumlah: 20, panjang: 6, prefix: '', charset: 'angka', samaPassword: true, paket_voucher_id: '', catatan: '' }
}

function defaultCustomForm() {
  return { kode: '', password: '', paket_voucher_id: '', harga_jual: '', catatan: '' }
}

// Password acak sisi klien -- cuma buat kemudahan isi form "Buat Username" (beda dari generateKode
// di server yg dipakai /generate massal & sudah jaga keunikan kode voucher acak).
function acakPassword() {
  const set = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 8; i++) out += set[Math.floor(Math.random() * set.length)]
  return out
}

export default function Voucher() {
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [paketFilter, setPaketFilter] = useState('all')
  const [paketList, setPaketList] = useState([])
  // Pilihan checkbox (2026-07-25) -- utk cetak kolektif/per-baris, lihat cetakByIds. Direset tiap
  // kali data halaman berganti (page/filter) -- id di halaman lain tidak kelihatan jadi
  // membingungkan kalau tetap "kecentang" tanpa terlihat.
  const [selected, setSelected] = useState(new Set())

  const [modal, setModal] = useState(false)
  const [gen, setGen] = useState(defaultGenForm())
  const [genError, setGenError] = useState('')
  const [generating, setGenerating] = useState(false)
  const [hasil, setHasil] = useState(null) // { sukses, gagal, catatan }

  // "Buat Username" (2026-07-27) -- beda modal/alur dari generate massal, lihat komentar di
  // routes/voucher.js's POST /custom.
  const [customModal, setCustomModal] = useState(false)
  const [custom, setCustom] = useState(defaultCustomForm())
  const [customError, setCustomError] = useState('')
  const [customSaving, setCustomSaving] = useState(false)

  useEffect(() => { loadPaket() }, [])
  useEffect(() => { load() }, [page, statusFilter, paketFilter])

  async function loadPaket() {
    const res = await api.get('/paket-voucher')
    setPaketList(res.data.filter(p => p.aktif))
  }

  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/voucher', { params: { page, limit, search, status: statusFilter, paket_voucher_id: paketFilter } })
      setData(res.data.items)
      setTotal(res.data.total)
      setSelected(new Set())
    } finally { setLoading(false) }
  }

  function cariSubmit(e) {
    e.preventDefault()
    setPage(1); load()
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    setSelected(prev => prev.size === data.length ? new Set() : new Set(data.map(v => v.id)))
  }

  function bukaGenerate() {
    setGen({ ...defaultGenForm(), paket_voucher_id: paketList[0]?.id || '' })
    setGenError(''); setHasil(null); setModal(true)
  }

  async function generate() {
    setGenError(''); setGenerating(true)
    try {
      const res = await api.post('/voucher/generate', gen)
      setHasil(res.data)
      setPage(1); load()
    } catch (e) {
      setGenError(e.response?.data?.message || 'Gagal generate voucher')
    } finally { setGenerating(false) }
  }

  function bukaCustom() {
    const paketDefault = paketList[0]
    setCustom({ ...defaultCustomForm(), password: acakPassword(), paket_voucher_id: paketDefault?.id || '', harga_jual: paketDefault?.harga_jual ?? '' })
    setCustomError(''); setCustomModal(true)
  }

  function pilihPaketCustom(paketVoucherId) {
    const p = paketList.find(x => String(x.id) === String(paketVoucherId))
    setCustom({ ...custom, paket_voucher_id: paketVoucherId, harga_jual: p?.harga_jual ?? custom.harga_jual })
  }

  async function simpanCustom() {
    setCustomError(''); setCustomSaving(true)
    try {
      await api.post('/voucher/custom', custom)
      setCustomModal(false)
      setPage(1); load()
    } catch (e) {
      setCustomError(e.response?.data?.message || 'Gagal membuat username')
    } finally { setCustomSaving(false) }
  }

  async function hapus(id) {
    if (!window.confirm('Nonaktifkan voucher ini?')) return
    await api.delete(`/voucher/${id}`)
    load()
  }

  async function renew(id) {
    if (!window.confirm('Aktifkan kembali voucher ini? Kode & password tetap sama.')) return
    await api.post(`/voucher/${id}/renew`)
    load()
  }

  // Cetak per-baris/kolektif (2026-07-25) -- lewat daftar ID spesifik, BUKAN catatan (itu selalu
  // ambil SATU BATCH utuh, dulu dipakai jg utk cetak per-baris -- itu bug-nya: klik Cetak di 1
  // baris keliru nyetak seluruh batch generate-nya). cetakBatch (via catatan) tetap dipakai KHUSUS
  // tombol Cetak di modal hasil generate (di situ maksudnya memang "cetak semua yg baru dibuat").
  function cetakByIds(ids) {
    if (ids.length === 0) return
    window.open(`/voucher/cetak?ids=${ids.join(',')}`, '_blank')
  }

  function cetakBatch(catatan) {
    window.open(`/voucher/cetak?catatan=${encodeURIComponent(catatan)}`, '_blank')
  }

  return (
    <div>
      <PageHeader
        title="Voucher"
        subtitle="Generate & kelola voucher hotspot"
        actions={(
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="soft" icon={UserPlus} onClick={bukaCustom}>Buat Username</Button>
            <Button icon={Plus} onClick={bukaGenerate}>Buat Voucher</Button>
          </div>
        )}
      />

      <form onSubmit={cariSubmit} style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari kode..." style={{ maxWidth: 220 }} />
        <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }} style={{ maxWidth: 160 }}>
          <option value="all">Semua Status</option>
          <option value="AKTIF">Aktif</option>
          <option value="NONAKTIF">Nonaktif</option>
        </Select>
        <Select value={paketFilter} onChange={e => { setPaketFilter(e.target.value); setPage(1) }} style={{ maxWidth: 200 }}>
          <option value="all">Semua Paket</option>
          {paketList.map(p => <option key={p.id} value={p.id}>{p.nama}</option>)}
        </Select>
        <Button type="submit" variant="soft">Cari</Button>
        {selected.size > 0 && (
          <Button icon={Printer} onClick={() => cetakByIds([...selected])} style={{ marginLeft: 'auto' }}>
            Cetak Terpilih ({selected.size})
          </Button>
        )}
      </form>

      <Table>
        {/* thead manual (bukan <THead cols={...}/>) -- kolom pertama butuh checkbox "pilih semua",
            THead cuma dirancang utk isi string/label (dipakai `h` langsung sbg React key). */}
        <thead>
          <tr style={{ background: '#fafafc' }}>
            <th style={{ padding: '11px 16px', borderBottom: '1px solid var(--border)', width: 1 }}>
              <input type="checkbox" checked={data.length > 0 && selected.size === data.length} onChange={toggleSelectAll} />
            </th>
            {['Kode', 'Password', 'Paket', 'Harga', 'Status', 'Catatan', 'Dibuat', ''].map(h => (
              <th key={h} style={{
                padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11.5,
                color: 'var(--text-muted)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.3,
                borderBottom: '1px solid var(--border)'
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={9}><EmptyState icon={Ticket} title="Memuat..." /></td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={9}><EmptyState icon={Ticket} title="Belum ada voucher" hint="Klik Buat Voucher utk generate massal" /></td></tr>
          ) : data.map(v => (
            <tr key={v.id}>
              <td style={td}><input type="checkbox" checked={selected.has(v.id)} onChange={() => toggleSelect(v.id)} /></td>
              <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600 }}>{v.kode}</td>
              <td style={{ ...td, fontFamily: 'monospace' }}>{v.password}</td>
              <td style={td}>{v.nama_paket || '-'}</td>
              <td style={td}>{rp(v.harga_jual)}</td>
              <td style={td}><Badge tone={STATUS_TONE[v.status]}>{v.status}</Badge></td>
              <td style={{ ...td, fontSize: 11.5, color: 'var(--text-faint)' }}>{v.catatan || '-'}</td>
              <td style={{ ...td, fontSize: 11.5, color: 'var(--text-faint)' }}>{v.created_at}</td>
              <td style={td}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" variant="soft" icon={Printer} onClick={() => cetakByIds([v.id])} />
                  {v.status === 'AKTIF'
                    ? <Button size="sm" variant="danger-soft" icon={Trash2} onClick={() => hapus(v.id)} />
                    : <Button size="sm" variant="soft" icon={RotateCcw} onClick={() => renew(v.id)}>Renew</Button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pagination page={page} limit={limit} total={total} onChange={setPage} />

      {modal && (
        <Modal onClose={() => setModal(false)} width={460}>
          <ModalHeader title="Buat Voucher" subtitle="Generate voucher massal" onClose={() => setModal(false)} />

          {!hasil ? (
            <>
              {genError && <Alert type="error">{genError}</Alert>}
              <div className="grid-2" style={{ gap: 10, marginBottom: 14 }}>
                <Field label="Jumlah Voucher *">
                  <Input type="number" min="1" max="500" value={gen.jumlah} onChange={e => setGen({ ...gen, jumlah: e.target.value })} />
                </Field>
                <Field label="Panjang Karakter *">
                  <Input type="number" min="3" max="20" value={gen.panjang} onChange={e => setGen({ ...gen, panjang: e.target.value })} />
                </Field>
              </div>
              <Field label="Prefix (opsional)" style={{ marginBottom: 14 }}>
                <Input value={gen.prefix} onChange={e => setGen({ ...gen, prefix: e.target.value })} placeholder="mis. FZ-" />
              </Field>
              <Field label="Kombinasi Karakter *" style={{ marginBottom: 14 }}>
                <Select value={gen.charset} onChange={e => setGen({ ...gen, charset: e.target.value })}>
                  <option value="angka">Angka</option>
                  <option value="huruf">Huruf Besar</option>
                  <option value="campuran">Campuran (Huruf + Angka)</option>
                </Select>
              </Field>
              <Field label="Jenis Kata Sandi *" style={{ marginBottom: 14 }}>
                <Select value={gen.samaPassword ? '1' : '0'} onChange={e => setGen({ ...gen, samaPassword: e.target.value === '1' })}>
                  <option value="1">"Username" sama dengan "Password"</option>
                  <option value="0">"Username" berbeda dengan "Password"</option>
                </Select>
              </Field>
              <Field label="Paket *" style={{ marginBottom: 14 }}>
                <Select value={gen.paket_voucher_id} onChange={e => setGen({ ...gen, paket_voucher_id: e.target.value })}>
                  <option value="">Pilih paket...</option>
                  {paketList.map(p => <option key={p.id} value={p.id}>{p.nama} — {rp(p.harga_jual)}</option>)}
                </Select>
              </Field>
              <Field label="Catatan (opsional)" style={{ marginBottom: 22 }}>
                <Textarea rows={2} value={gen.catatan} onChange={e => setGen({ ...gen, catatan: e.target.value })} placeholder="Label batch, kosongkan utk otomatis" />
              </Field>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
                <Button onClick={generate} loading={generating} style={{ flex: 1, justifyContent: 'center' }}>Generate</Button>
              </div>
            </>
          ) : (
            <>
              <Alert type={hasil.gagal.length ? 'error' : 'success'}>
                {hasil.sukses} voucher berhasil dibuat{hasil.gagal.length > 0 ? `, ${hasil.gagal.length} gagal` : ''}.
              </Alert>
              {hasil.gagal.length > 0 && (
                <div style={{ maxHeight: 140, overflowY: 'auto', fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                  {hasil.gagal.map((g, i) => <div key={i}>#{g.nomor}: {g.alasan}</div>)}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Tutup</Button>
                {hasil.sukses > 0 && (
                  <Button icon={Printer} onClick={() => cetakBatch(hasil.catatan)} style={{ flex: 1, justifyContent: 'center' }}>Cetak</Button>
                )}
              </div>
            </>
          )}
        </Modal>
      )}

      {customModal && (
        <Modal onClose={() => setCustomModal(false)} width={440}>
          <ModalHeader title="Buat Username" subtitle="Satu voucher dgn username/password pilihan sendiri -- cocok utk member permanen" onClose={() => setCustomModal(false)} />
          {customError && <Alert type="error">{customError}</Alert>}
          <Field label="Username *" style={{ marginBottom: 14 }}>
            <Input value={custom.kode} onChange={e => setCustom({ ...custom, kode: e.target.value })} placeholder="mis. nama pelanggan, tanpa spasi" />
          </Field>
          <Field label="Password *" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input value={custom.password} onChange={e => setCustom({ ...custom, password: e.target.value })} style={{ flex: 1, fontFamily: 'monospace' }} />
              <Button type="button" variant="soft" icon={Shuffle} onClick={() => setCustom({ ...custom, password: acakPassword() })} />
            </div>
          </Field>
          <Field label="Paket *" style={{ marginBottom: 14 }}>
            <Select value={custom.paket_voucher_id} onChange={e => pilihPaketCustom(e.target.value)}>
              <option value="">Pilih paket...</option>
              {paketList.map(p => <option key={p.id} value={p.id}>{p.nama} — {p.durasi_menit === 0 ? 'Selamanya' : ''} {rp(p.harga_jual)}</option>)}
            </Select>
          </Field>
          <Field label="Harga Jual (Rp)" style={{ marginBottom: 14 }}>
            <Input type="number" value={custom.harga_jual} onChange={e => setCustom({ ...custom, harga_jual: e.target.value })} />
          </Field>
          <Field label="Catatan (opsional)" style={{ marginBottom: 22 }}>
            <Textarea rows={2} value={custom.catatan} onChange={e => setCustom({ ...custom, catatan: e.target.value })} placeholder="mis. nama pelanggan/keterangan member" />
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setCustomModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button onClick={simpanCustom} loading={customSaving} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
